import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  Paper,
  CircularProgress,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  Factory as FactoryIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import FullScreenLayout from './layouts/FullScreenLayout';
import { productionPlantsApi } from '../services/productionPlantsApi';

// Styled components
const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #e0e0e0',
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const BatchCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  backgroundColor: 
    status === 'pending' ? '#ff9800' :
    status === 'in_progress' ? '#2196f3' :
    status === 'completed' ? '#4caf50' :
    status === 'cancelled' ? '#f44336' :
    status === 'on_hold' ? '#9e9e9e' :
    '#757575',
  color: '#ffffff',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  border: '2px dashed #e0e0e0',
}));

const ProductionBatches = () => {
  const navigate = useNavigate();
  
  // State management
  const [batches, setBatches] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form state for batch dialog
  const [formData, setFormData] = useState({
    plant_id: '',
    description: '',
    requested_quantity: '',
    estimated_start_date: '',
    estimated_completion_date: '',
    notes: '',
    items: []
  });

  // Item input state
  const [itemInput, setItemInput] = useState('');

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: <WarningIcon />, color: '#ff9800' },
    { value: 'in_progress', label: 'In Progress', icon: <PlayArrowIcon />, color: '#2196f3' },
    { value: 'completed', label: 'Completed', icon: <CheckCircleIcon />, color: '#4caf50' },
    { value: 'cancelled', label: 'Cancelled', icon: <CancelIcon />, color: '#f44336' },
    { value: 'on_hold', label: 'On Hold', icon: <PauseIcon />, color: '#9e9e9e' }
  ];

  useEffect(() => {
    fetchBatches();
    fetchPlants();
  }, [page, rowsPerPage]);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await productionPlantsApi.getAllBatches({
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setBatches(response.data || []);
      setTotalCount(response.total || response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching batches:', error);
      showSnackbar('Failed to fetch production batches', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlants = async () => {
    try {
      const response = await productionPlantsApi.getAllPlants();
      setPlants(response.data || []);
    } catch (error) {
      console.error('Error fetching plants:', error);
      showSnackbar('Failed to fetch production plants', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (batch = null) => {
    if (batch) {
      setEditingBatch(batch);
      setFormData({
        plant_id: batch.plant_id || '',
        description: batch.description || '',
        requested_quantity: batch.requested_quantity || '',
        estimated_start_date: batch.estimated_start_date ? 
          new Date(batch.estimated_start_date).toISOString().slice(0, 16) : '',
        estimated_completion_date: batch.estimated_completion_date ? 
          new Date(batch.estimated_completion_date).toISOString().slice(0, 16) : '',
        notes: batch.notes || '',
        items: []
      });
    } else {
      setEditingBatch(null);
      setFormData({
        plant_id: '',
        description: '',
        requested_quantity: '',
        estimated_start_date: '',
        estimated_completion_date: '',
        notes: '',
        items: []
      });
    }
    setItemInput('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBatch(null);
    setFormData({
      plant_id: '',
      description: '',
      requested_quantity: '',
      estimated_start_date: '',
      estimated_completion_date: '',
      notes: '',
      items: []
    });
    setItemInput('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const parseItemInput = (input) => {
    const lines = input.trim().split('\n').filter(line => line.trim());
    const items = [];
    
    for (const line of lines) {
      const parts = line.trim().split(/\s+/);
      if (parts.length >= 3) {
        const item_id = parseInt(parts[0]);
        const quantity = parseInt(parts[1]);
        const item_type = parts[2].toLowerCase();
        
        if (!isNaN(item_id) && !isNaN(quantity) && quantity > 0 && 
            ['input', 'output', 'byproduct'].includes(item_type)) {
          items.push({ item_id, quantity, item_type });
        }
      }
    }
    
    return items;
  };

  const handleSubmit = async () => {
    try {
      if (!formData.plant_id) {
        showSnackbar('Plant is required', 'error');
        return;
      }
      
      if (!formData.requested_quantity || formData.requested_quantity <= 0) {
        showSnackbar('Requested quantity must be greater than 0', 'error');
        return;
      }

      const items = parseItemInput(itemInput);
      
      const submitData = {
        ...formData,
        requested_quantity: parseInt(formData.requested_quantity),
        estimated_start_date: formData.estimated_start_date || null,
        estimated_completion_date: formData.estimated_completion_date || null,
        items
      };

      if (editingBatch) {
        // For editing, we would need an update endpoint
        showSnackbar('Batch editing not yet implemented', 'warning');
      } else {
        const response = await productionPlantsApi.createBatch(submitData);
        
        if (response.success) {
          showSnackbar(response.message || 'Production batch created successfully');
          
          // Show warnings if any unrecognized items
          if (response.warnings && response.warnings.length > 0) {
            setTimeout(() => {
              showSnackbar(response.warnings.join(', '), 'warning');
            }, 2000);
          }
        } else {
          showSnackbar(response.message || 'Failed to create batch', 'error');
        }
      }

      handleCloseDialog();
      fetchBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to save batch',
        'error'
      );
    }
  };

  const handleStatusUpdate = async (batchId, newStatus) => {
    try {
      const response = await productionPlantsApi.updateBatchStatus(batchId, { status: newStatus });
      if (response.success) {
        showSnackbar(`Batch status updated to ${newStatus}`);
        fetchBatches();
      }
    } catch (error) {
      console.error('Error updating batch status:', error);
      showSnackbar('Failed to update batch status', 'error');
    }
  };

  const handleViewBatch = (batchId) => {
    navigate(`/production-batches/${batchId}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.icon : <WarningIcon />;
  };

  const renderBatchCard = (batch) => (
    <BatchCard key={batch.batch_id}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="h6" fontWeight={600} color="#000000">
                {batch.batch_number}
              </Typography>
              <StyledChip
                label={batch.status}
                status={batch.status}
                icon={getStatusIcon(batch.status)}
                size="small"
              />
            </Box>
            {batch.description && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {batch.description}
              </Typography>
            )}
            <Box display="flex" alignItems="center" gap={3} mb={1}>
              <Typography variant="body2" color="text.secondary">
                Plant: {batch.plant_name || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quantity: {batch.requested_quantity}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items: {batch.total_items || 0}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(batch.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewBatch(batch.batch_id)}
            sx={{ color: '#000000' }}
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>
        {batch.status === 'pending' && (
          <Tooltip title="Start Production">
            <IconButton
              size="small"
              onClick={() => handleStatusUpdate(batch.batch_id, 'in_progress')}
              sx={{ color: '#2196f3' }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>
        )}
        {batch.status === 'in_progress' && (
          <Tooltip title="Complete Production">
            <IconButton
              size="small"
              onClick={() => handleStatusUpdate(batch.batch_id, 'completed')}
              sx={{ color: '#4caf50' }}
            >
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </BatchCard>
  );

  const header = (
    <HeaderContainer>
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={() => navigate('/production-plants')}
          sx={{ color: '#000000' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#000000">
            Production Batches
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage production batches and track progress
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{
          backgroundColor: '#000000',
          color: '#ffffff',
          fontWeight: 600,
          '&:hover': { backgroundColor: '#333333' },
        }}
      >
        Create Batch
      </Button>
    </HeaderContainer>
  );

  const mainContent = (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress sx={{ color: '#000000' }} />
        </Box>
      ) : batches.length === 0 ? (
        <EmptyState>
          <FactoryIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Production Batches Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create your first production batch to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#333333' },
            }}
          >
            Create First Batch
          </Button>
        </EmptyState>
      ) : (
        <>
          <Grid container spacing={3}>
            {batches.map((batch) => (
              <Grid item xs={12} md={6} lg={4} key={batch.batch_id}>
                {renderBatchCard(batch)}
              </Grid>
            ))}
          </Grid>
          
          <Box mt={3}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <FullScreenLayout
        header={header}
        mainPanel={mainContent}
      />

      {/* Create/Edit Batch Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editingBatch ? 'Edit Batch' : 'Create New Production Batch'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Production Plant</InputLabel>
                <Select
                  value={formData.plant_id}
                  onChange={(e) => handleFormChange('plant_id', e.target.value)}
                  label="Production Plant"
                >
                  {plants.map((plant) => (
                    <MenuItem key={plant.plant_id} value={plant.plant_id}>
                      {plant.name} {plant.location && `(${plant.location})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Requested Quantity"
                type="number"
                value={formData.requested_quantity}
                onChange={(e) => handleFormChange('requested_quantity', e.target.value)}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estimated Start Date"
                type="datetime-local"
                value={formData.estimated_start_date}
                onChange={(e) => handleFormChange('estimated_start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estimated Completion Date"
                type="datetime-local"
                value={formData.estimated_completion_date}
                onChange={(e) => handleFormChange('estimated_completion_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Items (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter one item per line in format: ItemID Quantity Type
                <br />
                Example: 123 50 input
                <br />
                Types: input, output, byproduct
              </Typography>
              <TextField
                fullWidth
                label="Items"
                value={itemInput}
                onChange={(e) => setItemInput(e.target.value)}
                multiline
                rows={6}
                placeholder="123 50 input&#10;456 25 output&#10;789 10 byproduct"
                helperText="Each line: ItemID Quantity Type (e.g., 123 50 input)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: '#757575' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            {editingBatch ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductionBatches; 